import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import TopNav from "../components/TopNav";
import LiveOverlay from "../components/LiveOverlay";
import TeamLink from "../components/TeamLink";
import { Calendar, Clock, ChevronDown, ChevronUp, AlertCircle, Sparkles, Trophy, Download, Loader2, Target } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { downloadNodeAsPng } from "../lib/slipToPng";
import { flagForCountry } from "../lib/countryFlags";

const fmtKickoff = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      day: "numeric",
      month: "short",
    });
  } catch {
    return iso;
  }
};

const fmtDate = (yyyymmdd) => {
  if (!yyyymmdd) return "";
  try {
    // Iter 110 — Build the Date in the user's LOCAL calendar (not UTC
    // midnight) so a slip dated "2026-05-10" always renders as the
    // weekday for May 10 regardless of the visitor's timezone.  The
    // previous `${yyyymmdd}T00:00:00Z` parse rendered as Saturday for
    // anyone west of UTC because UTC midnight maps back to the prior
    // day in their local clock.
    const [y, m, d] = yyyymmdd.split("-").map(Number);
    if (!y || !m || !d) return yyyymmdd;
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  } catch {
    return yyyymmdd;
  }
};

const dayBucket = (dateStr, todayStr) => {
  if (dateStr === todayStr) return "Today";
  // Iter 110 — Compute diff in local-calendar days rather than UTC,
  // so a user west of UTC isn't told today's slip is "Yesterday".
  const parse = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  const today = parse(todayStr);
  const d = parse(dateStr);
  const diffDays = Math.round((today - d) / (1000 * 60 * 60 * 24));
  if (diffDays === -1) return "Tomorrow";
  if (diffDays === 1) return "Yesterday";
  if (diffDays > 1 && diffDays <= 7) return "Last 7 Days";
  if (diffDays < -1) return "Upcoming";
  return "Older";
};

const fmtRoi = (pct) => {
  if (pct === null || pct === undefined) return null;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
};

const RoiPill = ({ label, days, data, accent }) => {
  const settled = data?.settled_slips ?? 0;
  const pct = data?.roi_pct ?? null;
  const positive = pct !== null && pct >= 0;
  const tone = pct === null
    ? "border-border/60 text-muted-foreground bg-subtle/40"
    : positive
    ? "border-emerald-500/40 text-emerald-500 bg-emerald-500/10"
    : "border-rose-500/40 text-rose-500 bg-rose-500/10";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border ${tone} font-tabular`}
      title={`${label} · ${days}d · ${settled} settled slips · ${data?.profit_units?.toFixed(2) ?? 0}u profit`}
      data-testid={`roi-pill-${label.toLowerCase()}-${days}d`}
    >
      <span className={`text-[9px] uppercase tracking-wider font-bold ${accent || "text-muted-foreground"}`}>{label} {days}d</span>
      <strong className="text-[11px]">{pct === null ? "—" : fmtRoi(pct)}</strong>
    </span>
  );
};

const SlipCard = ({ slip, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [downloading, setDownloading] = useState(false);
  const captureRef = useRef(null);
  const status = (slip.status || "OPEN").toUpperCase();
  const isEvening = slip.slot === "evening";
  const isSmart = slip.slot === "smart";
  const tone =
    status === "WON"
      ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40"
      : status === "LOST"
      ? "bg-rose-500/15 text-rose-500 border-rose-500/40"
      : status === "SETTLED"
      ? "bg-slate-500/15 text-slate-500 border-slate-500/40"
      : "bg-amber-500/15 text-amber-500 border-amber-500/40";

  // Iter 130 — Transparent slip-win probability badge.
  //   ≥ 25% : emerald  (1 in 4 or better — strong)
  //   ≥ 10% : amber    (rare but reasonable)
  //   <  10% : rose    (lottery-ticket territory)
  const winProb = typeof slip.slip_win_probability_pct === "number"
    ? slip.slip_win_probability_pct
    : null;
  const winProbTone =
    winProb === null ? "border-border/60 text-muted-foreground bg-subtle/40"
    : winProb >= 25 ? "border-emerald-500/40 text-emerald-500 bg-emerald-500/10"
    : winProb >= 10 ? "border-amber-500/40 text-amber-500 bg-amber-500/10"
    : "border-rose-500/40 text-rose-500 bg-rose-500/10";

  const onDownload = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    if (!open) setOpen(true);
    setDownloading(true);
    await new Promise((r) => setTimeout(r, 250));
    const ok = await downloadNodeAsPng(
      captureRef.current,
      `ndakubet-${isSmart ? "smart" : isEvening ? "evening" : "slip"}-${slip.date || slip.slip_id}`,
    );
    setDownloading(false);
    if (ok) toast.success("Saved as PNG");
    else toast.error("Couldn't render PNG. Try again.");
  };

  return (
    <article
      ref={captureRef}
      className={`relative rounded-2xl border overflow-hidden ${
        isSmart
          ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/[0.07] via-card to-card ring-1 ring-emerald-500/20"
          : isEvening
          ? "border-indigo-500/30 bg-gradient-to-br from-indigo-500/5 via-card to-card"
          : "border-border bg-card"
      }`}
      data-testid={`slip-card-${slip.slip_id}`}
    >
      {/* Download PNG — floated above the toggle row to avoid nested-button HTML */}
      <button
        type="button"
        onClick={onDownload}
        disabled={downloading}
        className="absolute top-3 right-3 z-10 p-1.5 rounded-md bg-card/80 backdrop-blur-sm border border-border/60 hover:bg-muted text-muted-foreground disabled:opacity-50 transition-colors"
        title="Download as PNG"
        data-testid={`slip-download-png-${slip.slip_id}`}
        data-png-skip="1"
      >
        {downloading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
      </button>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-muted/30 transition-colors"
        data-testid={`slip-toggle-${slip.slip_id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {isSmart ? (
            <Target className="w-5 h-5 text-emerald-400 shrink-0" />
          ) : isEvening ? (
            <Trophy className="w-5 h-5 text-indigo-400 shrink-0" />
          ) : (
            <Sparkles className="w-5 h-5 text-amber-500 shrink-0" />
          )}
          <div className="text-left min-w-0">
            <div className="font-display font-semibold text-base truncate flex items-center gap-2 flex-wrap">
              {fmtDate(slip.date)} ·{" "}
              {isSmart ? "Smart 5-Leg Slip" : isEvening ? "Tonight's Bonus Slip" : "NDAKUBET Curated Slip"}
              {isSmart && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border border-emerald-500/40 bg-emerald-500/15 text-emerald-400">
                  Smart
                </span>
              )}
              {isEvening && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border border-indigo-500/40 bg-indigo-500/15 text-indigo-400">
                  Evening
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
              <span><strong className="text-foreground font-tabular">{slip.legs_count}</strong> legs</span>
              <span>· avg <strong className="text-foreground font-tabular">{slip.avg_leg_confidence?.toFixed(1) ?? "—"}%</strong></span>
              <span>· combined <strong className="text-foreground font-tabular">@{slip.combined_odds?.toFixed(2) ?? "—"}</strong></span>
              {winProb !== null && (
                <span
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-tabular text-[10px] uppercase tracking-wider font-bold ${winProbTone}`}
                  title={`Probability that all ${slip.legs_count} legs hit (per-leg confidence multiplied together)`}
                  data-testid={`slip-win-prob-${slip.slip_id}`}
                >
                  Win chance {winProb >= 1 ? winProb.toFixed(0) : winProb.toFixed(2)}%
                </span>
              )}
              {slip.leg_rollup && (
                <span className="flex items-center gap-2">
                  <span className="text-emerald-500 font-tabular" data-testid={`slip-rollup-won-${slip.slip_id}`}>✓{slip.leg_rollup.won}</span>
                  <span className="text-rose-500 font-tabular" data-testid={`slip-rollup-lost-${slip.slip_id}`}>✗{slip.leg_rollup.lost}</span>
                  {slip.leg_rollup.live > 0 && (
                    <span className="text-red-500 font-tabular flex items-center gap-1" data-testid={`slip-rollup-live-${slip.slip_id}`}>
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span>
                      </span>
                      LIVE {slip.leg_rollup.live}
                    </span>
                  )}
                  {slip.leg_rollup.pending > 0 && (
                    <span className="text-amber-500 font-tabular" data-testid={`slip-rollup-pending-${slip.slip_id}`}>⏳{slip.leg_rollup.pending}</span>
                  )}
                  {slip.leg_rollup.void > 0 && (
                    <span className="text-slate-500 font-tabular" data-testid={`slip-rollup-void-${slip.slip_id}`}>∅{slip.leg_rollup.void}</span>
                  )}
                </span>
              )}
            </div>
            {slip.market_mix && Object.keys(slip.market_mix).length > 0 && (
              <div className="text-[11px] text-muted-foreground/80 mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`slip-market-mix-${slip.slip_id}`}>
                {Object.entries(slip.market_mix)
                  .sort((a, b) => b[1] - a[1])
                  .map(([mt, n]) => (
                    <span key={mt} className="px-1.5 py-0.5 rounded border border-border/60 bg-subtle/40 font-tabular">
                      {mt.replace("Both Teams to Score (BTTS)", "BTTS").replace("Draw No Bet (DNB)", "DNB").replace("HT Both Teams to Score (BTTS)", "HT BTTS")} ×{n}
                    </span>
                  ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0 pr-10">
          <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border ${tone}`}>{status}</span>
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-border bg-subtle/40">
          {slip.legs?.length ? (
              <ol className="divide-y divide-border" data-testid={`slip-legs-${slip.slip_id}`}>
                {slip.legs.map((leg, i) => {
                  const settle = leg.settlement?.status || "PENDING";
                  const pillTone =
                    settle === "WON"
                      ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40"
                      : settle === "LOST"
                      ? "bg-rose-500/15 text-rose-500 border-rose-500/40"
                      : settle === "VOID"
                      ? "bg-slate-500/15 text-slate-500 border-slate-500/40"
                      : "bg-amber-500/15 text-amber-500 border-amber-500/40";
                  const pillLabel =
                    settle === "WON"
                      ? "✓ Won"
                      : settle === "LOST"
                      ? "✗ Lost"
                      : settle === "VOID"
                      ? "Void"
                      : "Pending";
                  const settledScore = leg.settlement?.actual_score;
                  return (
                    <li
                      key={`${slip.slip_id}-leg-${i}`}
                      className={`px-5 py-3 flex items-center gap-3 text-sm ${
                        settle === "LOST" ? "opacity-75" : ""
                      }`}
                      data-testid={`slip-leg-${slip.slip_id}-${i}-${settle.toLowerCase()}`}
                    >
                      <span className="font-tabular text-xs text-muted-foreground w-6 shrink-0">#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">
                            <TeamLink name={leg.home_team} /> <span className="text-muted-foreground">vs</span>{" "}
                            <TeamLink name={leg.away_team} />
                          </span>
                          <LiveOverlay fixtureId={leg.fixture_id} compact />
                          {settledScore && (
                            <span className="text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded border border-slate-500/40 bg-slate-500/10 text-slate-500 font-tabular">
                              FT {settledScore.home}-{settledScore.away}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                          <Clock className="w-3 h-3" />
                          <span>{fmtKickoff(leg.kickoff)}</span>
                          <span className="truncate flex items-center gap-1">
                            {/* Iter 118 — Country flag beside the league name.
                                Returns "" for unknown countries → graceful
                                omission rather than a tofu glyph. */}
                            <span aria-hidden="true">·</span>
                            {flagForCountry(leg.country) && (
                              <span
                                role="img"
                                aria-label={leg.country || ""}
                                title={leg.country || ""}
                                className="leading-none text-sm shrink-0"
                                data-testid={`slip-leg-flag-${leg.fixture_id}`}
                              >
                                {flagForCountry(leg.country)}
                              </span>
                            )}
                            <span>{leg.league}</span>
                          </span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{leg.market_type}</div>
                        <div className="font-display font-semibold text-sm">{leg.prediction}</div>
                        {/* Iter 120 — league_edge chip: historical hit rate for this (league, market)
                            combo from `market_calibration`. Surfaces *why* this leg was promoted. */}
                        {typeof leg.league_edge === "number" && leg.league_edge > 0 && (
                          <span
                            className={`inline-block mt-1 text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded border font-tabular ${
                              leg.league_edge >= 65
                                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                                : leg.league_edge >= 50
                                ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
                                : "border-rose-500/40 bg-rose-500/10 text-rose-400"
                            }`}
                            title={`Historical hit rate for ${leg.league} · ${leg.market_type}: ${Math.round(leg.league_edge)}% over ${leg.league_samples || 0} samples`}
                            data-testid={`slip-leg-edge-${leg.fixture_id}`}
                          >
                            Edge {Math.round(leg.league_edge)}%
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0 w-20">
                        <span
                          className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full border ${pillTone}`}
                          data-testid={`slip-leg-status-${leg.fixture_id}`}
                        >
                          {pillLabel}
                        </span>
                        <span className="font-tabular font-bold text-sm">{Math.round(leg.confidence)}%</span>
                      </div>
                    </li>
                  );
                })}
              </ol>
          ) : (
            <div className="px-5 py-6 text-center text-sm text-muted-foreground">
              No legs available for this slip.
            </div>
          )}
        </div>
      )}
    </article>
  );
};

const EveningPlaceholder = ({ date }) => {
  const nowUtcHour = new Date().getUTCHours();
  // 3 phases: before 16 UTC (waiting), 16-18 UTC (compiling), after 18 UTC (skipped — late kickoff window has dried up)
  const phase = nowUtcHour < 16 ? "waiting" : nowUtcHour < 18 ? "compiling" : "skipped";
  const chipLabel =
    phase === "waiting" ? "16:00 UTC"
    : phase === "compiling" ? "Compiling now…"
    : "Tomorrow @ 06:00 UTC";
  const chipTone =
    phase === "skipped"
      ? "border-slate-500/40 bg-slate-500/15 text-slate-400"
      : "border-amber-500/40 bg-amber-500/15 text-amber-500";
  const body =
    phase === "waiting"
      ? "10-leg slip for late European + South-American kickoffs. Auto-publishes at 16:00 UTC every day."
      : phase === "compiling"
      ? "Tonight's 10-leg bonus slip is being compiled — refresh in a minute."
      : "No bonus slip tonight — not enough late kickoffs left. The morning slip drops at 06:00 UTC.";
  return (
    <article
      className="rounded-2xl border border-dashed border-indigo-500/30 bg-gradient-to-br from-indigo-500/5 via-card/60 to-card/60 px-5 py-4 flex items-start gap-3"
      data-testid={`slip-evening-placeholder-${date}`}
    >
      <Trophy className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-semibold text-base flex items-center gap-2 flex-wrap">
          Tonight's Bonus Slip
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border border-indigo-500/40 bg-indigo-500/15 text-indigo-400">
            Evening
          </span>
          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${chipTone}`}>
            <Clock className="w-2.5 h-2.5" />
            {chipLabel}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {body}
        </p>
      </div>
    </article>
  );
};

const Group = ({ title, slips, defaultOpenFirst = false, eveningPlaceholderDate = null }) => {
  const hasEvening = slips.some((s) => s.slot === "evening");
  return (
    <section className="space-y-3" data-testid={`slip-group-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        <Calendar className="w-4 h-4" />
        {title}
        <span className="font-tabular text-foreground/80">
          · {slips.length}{eveningPlaceholderDate && !hasEvening ? " + 1 pending" : ""}
        </span>
      </div>
      <div className="grid gap-3">
        {slips.map((s, i) => (
          <SlipCard
            key={`${s.slip_id}-${s.slot || "morning"}`}
            slip={s}
            defaultOpen={defaultOpenFirst && i === 0}
          />
        ))}
        {eveningPlaceholderDate && !hasEvening && (
          <EveningPlaceholder date={eveningPlaceholderDate} />
        )}
      </div>
    </section>
  );
};

export default function DailyBetslipsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [roi7, setRoi7] = useState(null);
  const [roi30, setRoi30] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth?next=/account/betslips");
      return;
    }
    setLoading(true);
    Promise.all([
      axios.get(`${API}/account/daily-slip/history`, { params: { days: 30 } }),
      axios.get(`${API}/account/daily-slip/roi`, { params: { days: 7 } }),
      axios.get(`${API}/account/daily-slip/roi`, { params: { days: 30 } }),
    ])
      .then(([h, r7, r30]) => {
        // Iter 113c — Filter out 0-leg slips before grouping.  These can
        // appear briefly between scheduled cron compiles (e.g. the
        // evening slot before 16:00 UTC has run) and they render as
        // confusing "0 legs · avg —%" cards.  When the slot is
        // genuinely missing, `Group` already renders an
        // `EveningPlaceholder` ("Tonight's slip drops at 16:00 UTC").
        const raw = h.data?.slips || [];
        const usable = raw.filter((s) => (s.legs_count ?? (s.legs?.length || 0)) > 0);
        setHistory(usable);
        setRoi7(r7.data || null);
        setRoi30(r30.data || null);
      })
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  // Iter 110 — Use the user's LOCAL calendar date so the "Today" /
  // "Yesterday" / "Tomorrow" buckets match what they see on their
  // wall clock, regardless of timezone offset.  The slip dates from
  // the backend are UTC-anchored YYYY-MM-DD strings, but for the
  // bucket-name UX we want local-day semantics.
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const grouped = {
    Upcoming: [],
    Tomorrow: [],
    Today: [],
    Yesterday: [],
    "Last 7 Days": [],
    Older: [],
  };
  for (const s of history) {
    const b = dayBucket(s.date, todayStr);
    if (grouped[b]) grouped[b].push(s);
    else grouped.Older.push(s);
  }

  return (
    <div className="min-h-screen bg-background">
      <TopNav />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <header className="space-y-3 nd-fade-up">
          <span className="eyebrow">Your Betslips</span>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                Daily Curated Slips
              </h1>
              <p className="text-base text-muted-foreground mt-2 max-w-2xl">
                Every morning at 06:00 UTC we hand you three slips: a tight{" "}
                <span className="text-emerald-400 font-medium">Smart 5-Leg</span> for the best
                shot at cashing, a 20-leg <span className="text-amber-500 font-medium">Curated
                Slip</span> for big-multiplier bankers, and a 10-leg{" "}
                <span className="text-indigo-400 font-medium">Tonight's Bonus Slip</span> at
                16:00 UTC for late kickoffs. Every slip card shows the true probability all legs hit.
              </p>
            </div>
            <Badge className="bg-amber-500/10 text-amber-500 border border-amber-500/40 text-xs font-bold tracking-wider">
              <Trophy className="w-3 h-3 mr-1" /> AUTO-CURATED
            </Badge>
          </div>
          {(roi7 || roi30) && (
            <div
              className="mt-3 rounded-xl border border-border bg-card/50 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2"
              data-testid="roi-tracker"
            >
              <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-muted-foreground/80">ROI Tracker</span>
              <span className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase font-bold text-emerald-500">Smart</span>
                <RoiPill label="SMART" days={7} data={roi7?.smart} accent="text-emerald-500" />
                <RoiPill label="SMART" days={30} data={roi30?.smart} accent="text-emerald-500" />
              </span>
              <span className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase font-bold text-amber-500/90">Morning</span>
                <RoiPill label="MORNING" days={7} data={roi7?.morning} accent="text-amber-500" />
                <RoiPill label="MORNING" days={30} data={roi30?.morning} accent="text-amber-500" />
              </span>
              <span className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase font-bold text-indigo-400">Evening</span>
                <RoiPill label="EVENING" days={7} data={roi7?.evening} accent="text-indigo-400" />
                <RoiPill label="EVENING" days={30} data={roi30?.evening} accent="text-indigo-400" />
              </span>
              <span className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase font-bold text-foreground/70">All Slips</span>
                <RoiPill label="ALL" days={30} data={roi30?.all} accent="text-foreground/70" />
              </span>
            </div>
          )}
        </header>

        {loading && (
          <div
            className="rounded-2xl border border-border bg-card px-6 py-12 text-center text-sm text-muted-foreground"
            data-testid="slips-loading"
          >
            Loading your slips…
          </div>
        )}

        {error && (
          <div
            className="rounded-2xl border border-rose-500/40 bg-rose-500/5 px-4 py-4 text-sm text-rose-500 flex items-center gap-2"
            data-testid="slips-error"
          >
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {!loading && !error && history.length === 0 && (
          <div
            className="rounded-2xl border border-dashed border-border bg-muted/20 px-6 py-12 text-center text-sm text-muted-foreground"
            data-testid="slips-empty"
          >
            No slips compiled yet — check back after 06:00 UTC, or your first slip will be
            generated on demand the next time you visit.
          </div>
        )}

        {!loading && !error && history.length > 0 && (
          <div className="space-y-8">
            {grouped.Upcoming.length > 0 && (
              <Group title="Upcoming" slips={grouped.Upcoming} defaultOpenFirst />
            )}
            {grouped.Tomorrow.length > 0 && (
              <Group title="Tomorrow" slips={grouped.Tomorrow} defaultOpenFirst />
            )}
            {grouped.Today.length > 0 && (
              <Group
                title="Today"
                slips={grouped.Today}
                defaultOpenFirst
                eveningPlaceholderDate={grouped.Today[0]?.date || todayStr}
              />
            )}
            {grouped.Yesterday.length > 0 && <Group title="Yesterday" slips={grouped.Yesterday} />}
            {grouped["Last 7 Days"].length > 0 && (
              <Group title="Last 7 Days" slips={grouped["Last 7 Days"]} />
            )}
            {grouped.Older.length > 0 && <Group title="Older" slips={grouped.Older} />}
          </div>
        )}
      </main>
    </div>
  );
}
